import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { LeftChevron } from '@williamhill/rosi-ui-shared/src/assets/icons';
import ResponsibleGamblingComponent from '../responsible-gambling-component';
import { queryAndAddTicket, useServices } from '../services';
import { validation } from '../services';
import TicketEntry, { SECTION_LENGTH } from './ticket-entry';
import { useTicketSearchNavigation, useBetsNavigation } from '../navigation';
import { withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import useTicketSearchContext from './use-ticket-search-context';
import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';
import Header from '../components/header';
import { robotoFonts } from '@williamhill/rosi-ui-shared/src/components/typography';

const { validateRetailTicketId } = validation;

const withTestId = withRootTestId('qa-bet-tracker-ticket-entry');
const logger = getLogger('bet-tracker/manual-entry');

export default function ManualEntryComponent() {
  const { loading: loadingServices, americanWageringGateway, dynamicConfig } = useServices();
  const { context: ticketSearchContext, clearContext } = useTicketSearchContext();
  const { goToTicketSearchOrigin } = useTicketSearchNavigation();

  const { goToTicket } = useBetsNavigation();
  const [ticketNumber, setTicketNumber] = useState<string | undefined>(
    filterAndMaskTicketNumber(ticketSearchContext?.ticketNumber)
  );
  const [error, setError] = useState<string | undefined>(ticketSearchContext?.error);
  const [abortController, setAbortController] = useState<AbortController | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (ticketSearchContext && !loadingServices) {
      handleSubmit();
    }
  }, [ticketSearchContext, loadingServices]);

  useEffect(() => clearContext, []);

  return (
    <>
      <Header />
      <BackLink data-testid={withTestId('back')} onClick={goBack}>
        <LeftChevron />
      </BackLink>
      <Section>
        <TicketEntry
          value={ticketNumber}
          onChange={handleChange}
          onClear={handleClear}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          error={error}
          isLoading={isLoading && !loadingServices}
        />
      </Section>
      <ResponsibleGamblingComponent />
    </>
  );

  function handleChange(value: string) {
    setTicketNumber(value);
    setError(undefined);
  }

  async function handleSubmit() {
    if (!ticketNumber) {
      return;
    }
    const retailTicketId = ticketNumber.replace(/-/g, '');
    const { isValid, errorMessage } = validateRetailTicketId(retailTicketId, dynamicConfig);
    if (!isValid) {
      setError('The entered number is incorrect or bet cannot be tracked, check the number has been entered correctly');
      logger.info(errorMessage);
      return;
    }

    try {
      if (loadingServices) {
        return;
      }

      const abortController = new AbortController();
      setAbortController(abortController);
      setIsLoading(true);
      const ticket = await queryAndAddTicket({
        retailTicketId,
        abortController,
        dynamicConfig,
        americanWageringGateway
      });
      goToTicket(ticket);
    } catch (error) {
      setError(error.message);
      setIsLoading(false);
    }
  }

  function handleClear() {
    setTicketNumber('');
    setError(undefined);
  }

  function handleCancel() {
    abortController?.abort();
  }

  function goBack() {
    goToTicketSearchOrigin(ticketSearchContext?.origin);
  }
}

function filterAndMaskTicketNumber(value: string | undefined) {
  if (!value) {
    return value;
  }

  const filteredValue = value.replace(/[^0-9.]/g, '');
  let index = 0;
  return SECTION_LENGTH.map(sl => {
    const nextSection = filteredValue.substring(index, Math.min(index + sl, filteredValue.length));
    index += nextSection.length;
    return nextSection;
  })
    .filter(s => s !== '')
    .join('-');
}

const BackLink = styled.div`
  padding: 1.55rem;
`;

const Section = styled.section`
  padding: 2.25rem 5.2rem;

  & > header {
    font-size: ${robotoFonts.lg};
    font-weight: 600;
  }

  & > main {
    padding-top: 0.7rem;
  }
`;
